// 메일 flat 문서 처리 순수 로직 — MailDocCanvas.vue 가 iframe 편집을 배선할 때 쓴다.
// 문자열 레벨 함수(DOM 불필요)는 node:test 로 검증한다. DOM 의존 함수(flatToChips 등)는
// 후속 마일스톤에서 브라우저 계층에 둔다.
// node:test + webpack 양쪽 사용을 위해 CommonJS 로 작성.
const tokens = require('./mailTokenRegex')

// outerHTML / documentElement.outerHTML 은 DOCTYPE 를 포함하지 않는다. 로드 시 원문
// DOCTYPE 를 캡처해 저장 시 재부착해야 수신측 quirks mode(간격 붕괴)를 막는다.
// 선행 whitespace/주석은 허용, 대소문자 무시.
const DOCTYPE_RE = /^\s*<!DOCTYPE[^>]*>/i

// flat 문서 맨 앞의 DOCTYPE 원문을 반환(없으면 ''). trim 하지 않고 원문 그대로.
function captureDoctype(flat) {
    if (!flat) return ''
    const m = flat.match(DOCTYPE_RE)
    return m ? m[0].replace(/^\s+/, '') : ''
}

// html 에 DOCTYPE 가 없고 doctype 이 주어지면 앞에 재부착한다. 이미 있으면 그대로.
// 프레임 문서(html/head 존재)인데 doctype 을 못 구했으면 표준 DOCTYPE 를 강제.
function ensureDoctype(html, doctype, isFramed) {
    if (!html) return html
    if (DOCTYPE_RE.test(html)) return html
    if (doctype) return doctype + '\n' + html
    if (isFramed) return '<!DOCTYPE html>\n' + html
    return html
}

// content-wrap class 를 태그 무관하게 감지(패턴 A 콘텐츠 경계). content-wrapper 등 오탐 방지.
const CONTENT_WRAP_RE = /class\s*=\s*["'][^"']*\bcontent-wrap\b[^"']*["']/i
// 프레임(완전 문서) 여부: DOCTYPE / <html> / <head> 중 하나라도 있으면 프레임으로 본다.
const FRAME_RE = /<!DOCTYPE\b|<html[\s>]|<head[\s>]/i

// 편집 대상 분기(staging 실측 패턴 A 69 / B 13 / 기타 12 기준):
//   'whole'         — DOCTYPE/html/head 없음(통짜 div, 패턴 B). 문서 전체가 콘텐츠 → 전체 편집.
//   'framed'        — 프레임 + content-wrap 존재(패턴 A). content-wrap 안쪽만 편집, 프레임 잠금.
//   'unknown-framed'— 프레임인데 content-wrap 없음. 콘텐츠 경계 불명 → WYSIWYG 위험 → source 폴백.
function detectContentBoundary(flat) {
    if (!flat) return 'whole'
    const isFramed = FRAME_RE.test(flat)
    if (!isFramed) return 'whole'
    return CONTENT_WRAP_RE.test(flat) ? 'framed' : 'unknown-framed'
}

// WYSIWYG 로 안전하게 열 수 있는가. unknown-framed(프레임인데 경계 불명)면 source 모드로 강등.
// whole/framed 는 편집 가능. (교차오염은 백엔드 CoW 가 처리하므로 여기서 판정하지 않는다.)
function isWysiwygEditable(flat) {
    return detectContentBoundary(flat) !== 'unknown-framed'
}

// ── M2: 편집 영역 선택 / 잠금 / 직렬화 ──────────────────────────────
// (doc = iframe.contentDocument. 컴포넌트가 넘겨준다. jsdom Document 로도 동작.)

// 사람이 편집할 영역(editRoot)을 고른다.
//   framed → content-wrap 요소만, whole → body 전체, unknown-framed → null(편집 불가).
function selectEditRoot(doc, boundary) {
    if (!doc) return null
    if (boundary === 'framed') {
        return doc.querySelector('.content-wrap') || null
    }
    if (boundary === 'whole') {
        return doc.body || null
    }
    return null
}

// 읽기 전용 미리보기는 편집 경계를 찾지 못해도 전체 문서를 안전하게 표시할 수 있다.
// framed/whole은 비주얼 편집과 같은 영역을 사용하고, unknown-framed만 body로 폴백한다.
function selectPreviewRoot(doc, boundary) {
    if (!doc) return null
    return selectEditRoot(doc, boundary) || doc.body || doc.documentElement || null
}

// editRoot 만 편집 가능하게 하고, 그 바깥(상단 스타일·하단 회사 명함 등 프레임)은
// 편집·선택을 막는다. framed 문서에서 프레임을 실수로 지우는 것을 방지.
function applyEditability(doc, editRoot, boundary) {
    if (!doc || !editRoot) return
    if (boundary === 'framed' && doc.body) {
        // body 를 통째로 잠그고 editRoot 만 다시 연다(중첩이라 editRoot 가 이긴다).
        doc.body.setAttribute('contenteditable', 'false')
        doc.body.style.userSelect = 'none'
        editRoot.setAttribute('contenteditable', 'true')
        editRoot.style.userSelect = 'text'
    } else {
        editRoot.setAttribute('contenteditable', 'true')
    }
}

// 노드가 특정 조상 안에 있는가(경계 가드 판정용).
function isNodeWithin(node, ancestor) {
    if (!node || !ancestor) return false
    let n = node
    while (n) {
        if (n === ancestor) return true
        n = n.parentNode
    }
    return false
}

// 편집 결과를 저장용 flat HTML 문자열로 되돌린다.
//   whole  → body.innerHTML (iframe 이 씌운 html/body 래퍼를 벗겨 원래 통짜 조각만).
//   framed → documentElement.outerHTML + DOCTYPE 재부착(완전 문서 보존).
// ※ 저장 직전 트리를 clone 해 칩(알약)을 원래 {{ }} 글자로 되돌린 뒤 직렬화한다
//   (원본 편집 DOM 은 칩 상태 유지). 브라우저 직렬화 정규화로 첫 저장에 미세 drift 가
//   날 수 있으나 두 번째 저장부터 안정(idempotent). 바이트 완전 보존은 M6.
function serializeDoc(doc, boundary, doctype) {
    if (!doc) return ''
    if (boundary === 'whole') {
        if (!doc.body) return ''
        const clone = doc.body.cloneNode(true)
        restoreChips(clone)
        stripEditingArtifacts(clone)
        return clone.innerHTML
    }
    if (!doc.documentElement) return ''
    const clone = doc.documentElement.cloneNode(true)
    restoreChips(clone)
    stripEditingArtifacts(clone)
    return ensureDoctype(clone.outerHTML, doctype, true)
}

// 저장 clone 에서 편집기 전용 아티팩트를 제거한다(발송 메일 오염 방지):
//   - 주입한 캔버스 스타일(<style data-mail-canvas> = 알약 CSS 등)
//   - contenteditable 속성(편집 영역 표시용)
//   - user-select inline(프레임 잠금용)
function stripEditingArtifacts(root) {
    if (!root || !root.querySelectorAll) return
    Array.prototype.forEach.call(root.querySelectorAll('style[data-mail-canvas]'), s => {
        if (s.parentNode) s.parentNode.removeChild(s)
    })
    Array.prototype.forEach.call(root.querySelectorAll('[contenteditable]'), el => {
        el.removeAttribute('contenteditable')
    })
    Array.prototype.forEach.call(root.querySelectorAll('[style*="user-select"]'), el => {
        if (el.style) el.style.removeProperty('user-select')
        if (el.getAttribute && !el.getAttribute('style')) el.removeAttribute('style')
    })
    // root 자신(body/documentElement)에 붙은 편집 속성도 제거
    if (root.removeAttribute) root.removeAttribute('contenteditable')
    if (root.style) root.style.removeProperty('user-select')
}

// ── M3: 변수/스니펫 자리를 알약(칩)으로 보이기 / 저장 시 원래 글자로 되돌리기 ──────
// 편집 중 {{ reserve user.e_mail }} 같은 자리를 파란 알약으로 보여주고(비편집·원자 단위),
// 저장 시 data-token 에 보관한 원문 글자로 정확히 복원한다. 텍스트에 있는 토큰만 알약으로
// 바꾸고, 속성 안 토큰(<a href="{{ origin }}">)은 절대 건드리지 않는다.

// TreeWalker/NodeFilter 상수(브라우저·jsdom 공통, 전역 미보장이라 리터럴 사용)
const SHOW_TEXT = 4
const FILTER_ACCEPT = 1
const FILTER_REJECT = 2

// 스니펫 인자에서 표시용 라벨(코드) 추출: 'welcome 3 x="y"' → 'welcome'
function snippetLabel(argsRaw) {
    const first = String(argsRaw || '').trim().split(/\s+/)[0]
    return first || 'snippet'
}

// 알약에 보일 글자. values(키→실제 값 맵)가 주어지고 var 알약이면 값을, 아니면 라벨(변수명).
function resolveChipText(label, kind, values) {
    const v = kind === 'var' && values ? values[label] : null
    return v == null || v === '' ? label : String(v)
}

// 알약 span 생성. data-token 에 원문 전체를 보존(복원 열쇠 — 절대 값으로 바꾸지 않는다),
// 화면엔 라벨 또는 실제 값(values 지정 시), hover(title)엔 토큰 원문.
function makeChip(doc, token, label, kind, values) {
    const span = doc.createElement('span')
    span.className = 'mail-var-chip'
    span.setAttribute('contenteditable', 'false')
    span.setAttribute('data-token', token)
    span.setAttribute('data-kind', kind)
    span.setAttribute('title', token)
    if (kind === 'var') span.setAttribute('data-key', label)
    span.textContent = resolveChipText(label, kind, values)
    return span
}

// 값이 바뀌었을 때(기한/URL 수정) 문서를 다시 쓰지 않고 var 알약의 글자만 갱신한다.
function refreshChipValues(doc, values) {
    if (!doc || !doc.querySelectorAll) return
    Array.prototype.forEach.call(doc.querySelectorAll('.mail-var-chip[data-key]'), chip => {
        chip.textContent = resolveChipText(chip.getAttribute('data-key'), 'var', values)
    })
}

// 텍스트 노드 하나를 [앞 텍스트 + 알약 + 뒤 텍스트]로 분해한다.
function chipifyTextNode(doc, textNode, values) {
    const text = textNode.nodeValue
    const matches = []
        .concat(tokens.findVarMatches(text).map(m => ({ index: m.index, length: m.length, full: m.full, label: m.key, kind: 'var' })))
        .concat(tokens.findSnippetMatches(text).map(m => ({ index: m.index, length: m.length, full: m.full, label: snippetLabel(m.argsRaw), kind: 'snippet' })))
        .sort((a, b) => a.index - b.index)
    if (!matches.length) return

    const frag = doc.createDocumentFragment()
    let pos = 0
    matches.forEach(m => {
        if (m.index < pos) return // 겹침 방어(VAR/SNIPPET 은 겹치지 않으나 안전)
        if (m.index > pos) frag.appendChild(doc.createTextNode(text.slice(pos, m.index)))
        frag.appendChild(makeChip(doc, m.full, m.label, m.kind, values))
        pos = m.index + m.length
    })
    if (pos < text.length) frag.appendChild(doc.createTextNode(text.slice(pos)))
    textNode.parentNode.replaceChild(frag, textNode)
}

// root(편집 영역) 안의 텍스트 노드에 있는 토큰만 알약으로 바꾼다. values 는 선택(알약에 실제 값 표시).
function flatToChips(root, doc, values) {
    if (!root) return
    doc = doc || root.ownerDocument
    if (!doc || !doc.createTreeWalker) return
    const walker = doc.createTreeWalker(root, SHOW_TEXT, {
        acceptNode(n) {
            const p = n.parentNode
            if (!p) return FILTER_REJECT
            const tag = p.nodeName
            if (tag === 'SCRIPT' || tag === 'STYLE') return FILTER_REJECT
            // 이미 알약 안이면 건드리지 않음
            if (p.closest && p.closest('.mail-var-chip')) return FILTER_REJECT
            return tokens.hasAnyToken(n.nodeValue) ? FILTER_ACCEPT : FILTER_REJECT
        },
    })
    const targets = []
    let n
    while ((n = walker.nextNode())) targets.push(n)
    targets.forEach(t => chipifyTextNode(doc, t, values))
}

// root 안의 알약을 data-token 원문 글자로 되돌린다(저장 직전 clone 에 적용).
function restoreChips(root) {
    if (!root || !root.querySelectorAll) return
    const doc = root.ownerDocument
    const chips = root.querySelectorAll('.mail-var-chip')
    Array.prototype.forEach.call(chips, chip => {
        const token = chip.getAttribute('data-token') || ''
        if (chip.parentNode) chip.parentNode.replaceChild(doc.createTextNode(token), chip)
    })
}

// 문자열 안 토큰(원문)들의 정렬된 목록 — 로드본과 저장본을 비교해 토큰 손실을 감지한다.
function tokenMultiset(str) {
    return []
        .concat(tokens.findVarMatches(str).map(m => m.full))
        .concat(tokens.findSnippetMatches(str).map(m => m.full))
        .sort()
}

// 저장본이 로드본의 토큰을 하나도 잃지 않았는지. 저장 게이트의 실질 안전망.
function tokensPreserved(originalFlat, savedFlat) {
    const a = tokenMultiset(originalFlat)
    const b = tokenMultiset(savedFlat)
    // 포함 관계: 원본의 각 토큰이 저장본에 그 개수 이상 남아 있으면 통과(새 변수 추가는 허용).
    const count = {}
    b.forEach(t => { count[t] = (count[t] || 0) + 1 })
    for (const t of a) {
        if (!count[t]) return false
        count[t] -= 1
    }
    return true
}

// ── M4: 버튼 스타일 직접 박기(used-class → inline) ──────────────────────────
// 패턴 A 프레임의 CTA 버튼/카드는 스타일이 head <style> 의 class 에만 있고 inline 이 0이라,
// Gmail(비-Google 계정)/Yahoo 가 <style> 를 지우면 버튼이 파란 밑줄 링크로 붕괴한다.
// 저장 시 실렌더(getComputedStyle) 기준으로 관심 프로퍼티만 요소 inline style 로 옮겨
// <style> 가 없어도 버튼이 유지되게 한다. 구조는 바꾸지 않고 스타일만 이동(프레임 보존).

// class 로만 스타일이 붙는 프레임 요소 셀렉터 + 인라인화할 프로퍼티.
const INLINE_TARGETS = [
    { sel: '.btn-authorization, .btn-authorization-allow, .btn-authorization-reject, .btn-invitation',
      props: ['background-color', 'color', 'padding', 'border-radius', 'text-decoration', 'display', 'font-size'] },
    { sel: '.content-wrap', props: ['padding', 'background-color'] },
    { sel: '.content-card', props: ['background-color', 'padding', 'border-radius'] },
]

// win = iframe.contentWindow(getComputedStyle 제공). root = 직렬화 대상 clone 이 아니라
// "살아있는" editRoot 의 조상 문서여야 computed 가 정확하다 → 저장 직전 live doc 에 적용 후
// serialize 한다. 값이 비었거나 'none'/'rgba(0,0,0,0)' 같은 무의미값은 건너뛴다.
function inlineFrameClasses(doc, win) {
    if (!doc || !win || !win.getComputedStyle || !doc.querySelectorAll) return
    INLINE_TARGETS.forEach(({ sel, props }) => {
        const els = doc.querySelectorAll(sel)
        Array.prototype.forEach.call(els, el => {
            const cs = win.getComputedStyle(el)
            props.forEach(prop => {
                const v = cs.getPropertyValue(prop)
                if (!v) return
                if (prop === 'background-color' && (v === 'rgba(0, 0, 0, 0)' || v === 'transparent')) return
                if (prop === 'text-decoration' && v.indexOf('none') === 0) return
                // 이미 inline 에 같은 프로퍼티가 있으면 사용자 지정 우선(덮지 않음).
                if (el.style.getPropertyValue(prop)) return
                el.style.setProperty(prop, v)
            })
        })
    })
}

module.exports = {
    DOCTYPE_RE,
    CONTENT_WRAP_RE,
    FRAME_RE,
    captureDoctype,
    ensureDoctype,
    detectContentBoundary,
    isWysiwygEditable,
    // M2
    selectEditRoot,
    selectPreviewRoot,
    applyEditability,
    isNodeWithin,
    serializeDoc,
    // M3
    snippetLabel,
    resolveChipText,
    makeChip,
    refreshChipValues,
    flatToChips,
    restoreChips,
    stripEditingArtifacts,
    tokenMultiset,
    tokensPreserved,
    // M4
    INLINE_TARGETS,
    inlineFrameClasses,
}
